import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ApAlertService, ApBaseComponent } from 'webcommon/core-ui';
import { FindControlIdService, WebCommonStorageRepository } from 'webcommon/legacy-common';
import {
  FindControlAdditionalSearchParametersDto,
  FindControlAdditionalSearchParametersType,
  FpMessageCreatedDto,
  FpMessagesCountsResultDto,
  FpMessagesReceivedDto,
  FpMessagesUrgencyDto,
  FpMessageTypeAndSuTypeParamsDto,
  FpReceivedMessageParamsDto,
  FpUserPropertiesDto,
  MessageSubTypeDto,
  SystemGuid,
} from 'webcommon/shared';
import { FpMessagesRepository } from 'webcommon/webapi';
import { FpUserSettingsService } from '../services/FpUserSettingsService';
import { FPMessageCreateModalService } from './fp-message-create-modal.service';

@Component({
  selector: 'fp-message',
  styleUrls: ['./fp-message.component.less'],
  templateUrl: './fp-message.component.html',
})

export class FPMessageComponent extends ApBaseComponent implements OnInit {
  @Input() baseId: string;
  @Input() unreadMessageCount: number;
  @Output() msgReload: EventEmitter<void> = new EventEmitter<void>();

  currentUserId: string | null;
  currentUserName: string | null;
  isActiveDirectoryUser = false;
  isExpanded = false;
  Id: string;
  loadError = false;
  messageFilterSelected: string[] = [];
  messageSubTypes: MessageSubTypeDto[] | null;
  messagesCountsResults: FpMessagesCountsResultDto[] | null;
  messagesUrgenciesResults: FpMessagesUrgencyDto[] | null;
  messageFilterAdditionSearchParameters: FindControlAdditionalSearchParametersDto | undefined;
  messagesReceived: FpMessagesReceivedDto[] | null;
  userProperties: FpUserPropertiesDto | null;
  rcvMsgParams: FpReceivedMessageParamsDto | null;
  showArchived = false;
  loadingMessageCounts = false; // message counts don't change when filters change
  loadingReceivedMessages = false; // received messages change when filters change
  // this.loading used for settings that don't change on any refresh

  constructor(public findControlIdService: FindControlIdService,
              private messagesRepository: FpMessagesRepository,
              private systemGuid: SystemGuid,
              private fpMessageCreateModalService: FPMessageCreateModalService,
              private webCommonStorageRepository: WebCommonStorageRepository,
              private alertService: ApAlertService,
              private fpUserSettingsService: FpUserSettingsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loading = true;
    this.isActiveDirectoryUser = this.webCommonStorageRepository.getIsActiveDirectoryUser();
    this.currentUserId = this.webCommonStorageRepository.getUserId();
    this.currentUserName = this.webCommonStorageRepository.getUserFullName();
    this.setId();
    this.loadSettings(); // load settings that don't change on refresh
    this.loadMessages(); // load messagges that change on refresh
  }

  loadSettings(): void {
    this.loading = true;
    const messageTypeAndSubTypeParams: FpMessageTypeAndSuTypeParamsDto[] = [
      {
        MessageSubTypeIds: [],
        MessageTypeId: this.systemGuid.FacilityPortalMessageType,
      },
      {
        MessageSubTypeIds: [this.systemGuid.AttachmentSubMessageType],
        MessageTypeId: this.systemGuid.AAMessageType,
      },
    ];
    forkJoin(
      [
        this.messagesRepository.getMessageSubTypesForMessageType(messageTypeAndSubTypeParams),
        this.fpUserSettingsService.getFpUserProperties(),
        this.messagesRepository.getMessageUrgencies(5)
      ]
    ).pipe(finalize(() => this.onLoadSettingsComplete()),
    ).subscribe(
      (data) => this.onLoadSettingsSuccess(data[0], data[1], data[2]),
      () => this.onLoadSettingsError(),
    );
  }

  loadMessages(): void {
    this.loadingMessageCounts = true;
    // this.isExpanded = false;

    // MessageSubType Find Control is just a place holder until a message filter is implemented
    this.messageFilterAdditionSearchParameters = {
      Type: FindControlAdditionalSearchParametersType.Standard,
      AdditionalSearchParameters: [
        {
          ColumnName: 'MessageTypeUid',
          Value: this.systemGuid.FacilityPortalMessageType,
        },
      ],
    };

    this.messagesRepository.getMessageCounts(this.systemGuid.FacilityPortalMessageType, 5).pipe(
      finalize(() => this.onLoadMessageCountsComplete()),
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => this.onLoadMessageCountsSuccess(result),
      () => this.onLoadMessageCountsError(),
    );

    this.LoadReceivedMessages();
  }

  private LoadReceivedMessages() {
    this.loadingReceivedMessages = true;
    this.rcvMsgParams = {
      Completed: this.showArchived,
      SelectedMessageSubTypeIds: this.messageFilterSelected,
    };
    this.messagesRepository.getReceivedMessages(this.rcvMsgParams).pipe(
      finalize(() => this.onLoadReceivedMessagesComplete()),
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => this.onLoadReceivedMessagesSuccess(result),
      () => this.onLoadReceivedMessagesError(),
    );
  }

  private onLoadSettingsSuccess(messageSubTypeList: MessageSubTypeDto[],
                                userProperties: FpUserPropertiesDto,
                                messageUrgencies: FpMessagesUrgencyDto[]) {
    this.messageSubTypes = messageSubTypeList;
    this.userProperties = userProperties;
    this.messagesUrgenciesResults = messageUrgencies;
  }

  private onLoadMessageCountsSuccess(result: FpMessagesCountsResultDto[]) {
    this.messagesCountsResults = result;
  }

  private onLoadReceivedMessagesSuccess(result: FpMessagesReceivedDto[]) {
    this.messagesReceived = result;
  }

  private onLoadSettingsComplete() {
    this.loading = false;
  }

  private onLoadMessageCountsComplete() {
    this.loadingMessageCounts = false;
  }

  private onLoadReceivedMessagesComplete() {
    this.loadingReceivedMessages = false;
  }

  private setId() {
    this.Id = `${this.baseId}-messages`;
  }

  onShowArchivedChanged(): void {
    this.LoadReceivedMessages();
  }

  onMessageSubTypeSelected() {
    this.LoadReceivedMessages();
  }

  onExpandMessageCountsPanelClick() {
    this.isExpanded = !this.isExpanded;
  }

  onSortClick() {
    // TODO : implement click event
  }

  onRefreshClick() {
    this.loadMessages();
    this.msgReload.emit();
  }

  onMoreVertClick() {
    // TODO : implement click event
  }

  onCreateNewMessageClick(messageSubType: MessageSubTypeDto) {
    this.messagesRepository.getDefaultMessageUrgency().pipe(
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => this.openNewMessageModal(messageSubType, result),
      () => this.onLoadDefaultMessageUrgencyError(),
    );
  }

  openNewMessageModal(messageSubType: MessageSubTypeDto, mesgDefaultUrgency: string) {
    if (this.currentUserName) {
      this.currentUserName = this.currentUserName.replace(/['"]+/g, '');
    }

    this.fpMessageCreateModalService.openModal(
      '',
      this.currentUserName,
      mesgDefaultUrgency,
      messageSubType.MessageTypeId,
      'FacilityPortal',
      messageSubType.Id,
      messageSubType.Name,
      this.userProperties,
    ).then((result: FpMessageCreatedDto) => {
        // User created message
        if (result) {
          this.loadMessages();
          this.msgReload.emit();
        }
      },
      () => {
        // User canceled modal
      },
    );
  }

  private onLoadSettingsError() {
    this.alert('An error has occurred and the Messages Tab could not be loaded.');
    this.loadError = true;
    this.messageSubTypes = null;
    this.userProperties = null;
    this.messagesUrgenciesResults = null;
  }

  private onLoadMessageCountsError() {
    this.alert('An error has occurred and the message counts could not be loaded.');
    this.loadError = true;
    this.messagesCountsResults = null;
  }

  private onLoadReceivedMessagesError() {
    this.alert('An error has occurred and the list of received messages could not be loaded.');
    this.loadError = true;
    this.messagesReceived = null;
  }

  private onLoadDefaultMessageUrgencyError() {
    this.alert('An error has occurred and the default message urgency could not be loaded.');
    this.loadError = true;
  }

  reload(dataSaved: boolean) {
    if (dataSaved) {
      this.loadMessages();
    } else {
      this.LoadReceivedMessages();
    }

    this.msgReload.emit();
  }

  private alert(text: string) {
    this.alertService.error(text);
  }
}
